/**
 * The motion pattern registry — the single declared source of truth for how
 * captured template markup animates, shared by the parser, the runtime, the
 * render/export surfaces and the residue gate.
 *
 * DRAFT (phase M0). Every entry below is derived from `data/motion-audit.json`;
 * the `evidence` field records what the corpus actually showed. Phase M1 adds
 * the interpreter (`motion-residue.js`) and the tests that hold this honest.
 *
 * This module is PURE DATA — no functions, no imports. Predicates are enums
 * interpreted by shared helpers. A callback here would let the parser, the
 * runtime and the gate drift apart; an enum cannot.
 *
 * Schema
 *   id            unique key
 *   markers       selectors that identify the library. A SELF match puts an
 *                 element in scope; descending from an animated wrapper does not.
 *   owner         'builder' for the ONE row the builder stamps itself at
 *                 emission time. Absent means the row describes markup a crawl
 *                 captured. The parser's baked-class pass, the runtime's
 *                 convention table and template-reveal detection all branch on
 *                 it, so "the builder never bakes its own reveal into stored
 *                 section data" is enforced rather than remembered.
 *   support       'runtime'  — a driver re-animates it
 *                 'static'   — collapsed to a correct resting state, no motion
 *                 'none'     — recognized, deliberately untouched
 *   strip         { property: valueKind } — remove when the value matches
 *   reset         { property: literal }   — rewrite to a canonical resting value
 *   preserve      [{ marker, property, as, valueKind }] — move captured residue
 *                 into a builder-owned custom property before stripping it;
 *                 `marker` indexes this entry's markers array
 *   removeNodes   selectors for nodes the library injected that carry no content
 *   neutralize    selectors for controls that no longer do anything
 *   stamp         { on, name, value } — attribute written on the host element so
 *                 CSS and the runtime can find the pattern
 *   staticFallback pure-data instructions consumed by motion-static-css.js;
 *                 marker fields index this entry's markers array so the fallback
 *                 cannot acquire a private copy of a registry selector
 *   scope         { site, sectionId } — restricts the entry to one section, for
 *                 markup that carries no class of its own
 *   reveal        [{ selector, add, remove }] — the no-JS visibility fallback the
 *                 parser bakes in, and the class table the runtime's observer
 *                 re-earns at scroll time. One source so the two cannot disagree.
 *                 Verified: no element in the corpus matches two reveal groups,
 *                 so group order carries no meaning.
 *
 * valueKind
 *   'any'             any value
 *   'translate-only'  a transform built only from translate functions, matrix,
 *                     or none — never scale, rotate, skew or perspective, which
 *                     are authored
 *   'numeric'         a bare number or length
 *   'zero-or-one'     0 or 1, the end states of a fade
 */

export const MOTION_PATTERNS = [
  {
    // The ONE builder-owned entry. Every other row describes markup a CRAWL
    // captured; this one describes markup the BUILDER stamps at emission time
    // from `state.entrance` (see shared/entrance-presets.js).
    //
    // `owner: 'builder'` is what keeps that distinction enforceable rather than
    // conventional. Three consumers read it:
    //   - the parser's baked-class pass SKIPS builder rows, so "stamped only at
    //     emission time" stays true and a corpus rebuild can never bake
    //     pb-revealed into a stored section;
    //   - the runtime's convention table flags the row `pb`, so the mode
    //     operations can act on builder rows without touching template ones;
    //   - template-reveal DETECTION excludes it, so a section carrying a
    //     builder entrance is not reported as using a template convention.
    //
    // census:false for the sticky-nav-reveal reason: this is not a library
    // found in a crawl, and counting it would put builder-stamped elements into
    // registered scope for the residue audit.
    id: 'pb-entrance',
    owner: 'builder',
    markers: ['[data-pb-reveal]'],
    support: 'runtime',
    strip: {},
    reset: {},
    census: false,
    reveal: [{ selector: '[data-pb-reveal]', add: ['pb-revealed'] }],
    evidence:
      'Builder-owned, not corpus-derived: 0 crawled sections carry data-pb-reveal, and none ever can — stripAllBuilderAttrs removes every data-pb-* attribute on the way out of the editor and the parser never writes one. The reveal row exists so the shared runtime re-earns pb-revealed at scroll time from the SAME table the emission path bakes it from, which is the invariant that keeps a builder entrance behaving like every other convention: baked visible, hidden only behind the ephemeral pb-reveal-armed class, and gated on the html[data-pb-motion-runtime] handshake so no runtime means nothing hidden.'
  },
  {
    id: 'aos',
    markers: ['[data-aos]'],
    support: 'runtime',
    strip: { opacity: 'zero-or-one', transition: 'any' },
    reset: {},
    reveal: [
      { selector: '[data-aos], .aos-init', add: ['aos-init', 'aos-animate'], init: ['aos-init'] }
    ],
    evidence:
      '24 sites, ~489 sections. Inline residue is opacity:1 + transition on banditconcrete (6 groups) — frozen post-reveal end state that pinned those elements statically visible and killed their real aos.css animations. Stripped as of M6: the above-the-fold failsafe now exists, so the runtime only re-arms elements that start below the viewport and an element inside a hidden drawer can no longer be stranded invisible. valueKind zero-or-one so an authored opacity:0.9 is untouched. The reveal group\'s init subset (aos-init) marks "runtime hooked" and is never stripped by the runtime.'
  },
  {
    id: 'reveal-class',
    markers: [
      '.reveal',
      '[data-reveal]',
      '[data-reveal-stagger]',
      '.reveal-fade-up',
      '.reveal-scale-in',
      '.stagger-parent',
      '.stagger-item'
    ],
    support: 'runtime',
    strip: {},
    reset: {},
    reveal: [
      {
        selector:
          '[data-reveal], [data-reveal-stagger], .reveal-fade-up, .reveal-scale-in, .stagger-parent, .stagger-item',
        add: ['is-visible', 'in-view']
      },
      { selector: '.reveal', add: ['visible', 'is-visible', 'in-view'] }
    ],
    evidence:
      '7 sites, 410 sections. Only inline residue is transition-delay (31 occurrences, digitalinnovlab) — a delay only takes effect while a transition runs, so it is harmless either way and is left alone. The start-state CSS for these conventions is live and the baked classes already keep content visible.'
  },
  {
    id: 'scroll-trigger',
    markers: ['.scroll-trigger'],
    support: 'runtime',
    strip: {},
    reset: {},
    reveal: [{ selector: '.scroll-trigger', remove: ['scroll-trigger--offscreen'] }],
    evidence:
      '1 site (leahdo), 176 sections. No inline residue. Its --animation-order stagger indices are authored config and classify as such.'
  },
  {
    id: 'animated-delay',
    markers: ['.animated[data-delay]'],
    support: 'runtime',
    strip: {},
    reset: {},
    reveal: [{ selector: '.animated[data-delay]', add: ['in-view'] }],
    evidence:
      '1 site (tcvisualsllc.com), 22 elements across 9 shipped sections. The site’s own convention: critical.css declares .animated{opacity:0} with .animated.in-view{opacity:1;transform:none}, and an IntersectionObserver in an UNCAPTURED external script (the crawl holds 14 script refs and zero .js files) added in-view at scroll time — so all but the 1–3 elements inside the crawler’s initial viewport shipped permanently invisible-but-clickable. Every occurrence carries data-delay, which is what keeps this marker off animate.css markup (turpinart’s "animated fadeIn" has no data-delay); redrock’s cs-link-animated never matched, classList matching is exact. The slide-* offsets and the transition are authored CSS and run when the runtime re-earns in-view below the fold; the script-applied data-delay stagger is cosmetic and not reconstructed. No inline residue — the script wrote classes, not styles.'
  },
  {
    id: 'word-rotator',
    markers: [
      '.rotating-text-wrapper[data-words]',
      '.rotating-text-wrapper[data-words] .rotating-text-word',
      '.rotating-text-wrapper[data-words] .rotating-text-word > span'
    ],
    support: 'runtime',
    preserve: [
      {
        marker: 0,
        property: 'width',
        as: '--pb-rotator-fallback-width',
        valueKind: 'numeric'
      }
    ],
    strip: {
      // The wrapper's JS-measured pixel width is moved into the preserve rule's
      // builder-owned variable before this declaration is stripped. The
      // fallback consumes that value so the absolute-positioned word keeps its
      // captured layout box even before the IF-4 driver runs.
      width: 'numeric',
      // Per-letter spans: frozen mid-animation end states (rotateX(±90/0),
      // opacity 0/1) written by the uncaptured rotator script. 'any' for
      // transform because rotateX is outside 'translate-only' and nothing on
      // these elements carries an authored inline transform.
      transform: 'any',
      opacity: 'zero-or-one',
      transition: 'any'
    },
    reset: {},
    stamp: {
      on: '.rotating-text-wrapper[data-words]',
      name: 'data-pb-motion',
      value: 'word-rotator'
    },
    staticFallback: {
      kind: 'word-rotator',
      hostMarker: 0,
      wordMarker: 1,
      letterMarker: 2,
      preserve: 0,
      armedAttribute: 'data-pb-rotator-armed',
      runtimeHandshakeAttribute: 'data-pb-motion-runtime'
    },
    evidence:
      '1 site (tcvisualsllc.com), the home rotating-headline section — dropped id-less by the parser until IF-1 and entering the corpus with IF-5. data-words carries the word list (JSON array attribute); the uncaptured script measured the wrapper (style="width: 65px"), split the current word into per-letter spans and froze them mid-animation (span opacity:0 + rotateX(-90deg) at rest per local.css, inline 1/0deg on the captured frame). Without cleanup the collapsed static state is a ZERO-WIDTH wrapper with invisible text: .rotating-text-word is position:absolute (out of flow) and the span rest state hides every letter. The preserve rule moves the measurement into --pb-rotator-fallback-width before the inline width and frozen frames are stripped; the registry-derived fallback consumes that value and exposes the captured word until the IF-4 driver takes ownership per element (reduced motion / webdriver / reorder never arm — one static word, by protocol).'
  },
  {
    id: 'card-tilt',
    markers: ['[data-fx-bound]'],
    support: 'none',
    knownGap: 'card-tilt',
    strip: {},
    reset: {},
    evidence:
      '1 site (tcvisualsllc.com), the booking-page package cards. A mousemove script wrote --mx/--my/--rx/--ry custom properties for a 3D hover tilt (the stylesheet consumes them via var()); the captured inline values are the NEUTRAL resting state (--rx/--ry: 0deg, --mx/--my centered), so the frozen markup already renders correctly and stripping would change nothing. Reconstruction deliberately deferred (ingest-fidelity plan rev 2, user-locked): cosmetic hover polish driven by pointer math. data-fx-bound is the binding marker the site script stamped; recognized so a future crawl variant fails the gate loudly instead of shipping half a tilt.'
  },
  {
    id: 'mos',
    markers: ['[data-mos]'],
    support: 'runtime',
    strip: { transform: 'translate-only' },
    reset: {},
    evidence:
      '1 site (valc-conseil), 29 sections, 82 frozen transforms — translateY(20px)/translateX(±20px) reveal offsets. The hiding rule is .use-animations [data-mos^=fade]{opacity:0}, and .use-animations is a <html> class the crawl dropped, so nothing is invisible; the elements simply sit 20px out of place.'
  },
  {
    id: 'parallax-var',
    markers: ['.landscape:has(> .landscape_layer)', '.landscape_layer'],
    support: 'runtime',
    strip: { transform: 'translate-only', transition: 'any', '--scrollPos': 'any' },
    reset: {},
    driveVar: '--scrollPos',
    amplitudeAttr: 'data-offset',
    stamp: {
      on: '.landscape:has(> .landscape_layer)',
      name: 'data-pb-motion',
      value: 'parallax-var'
    },
    evidence:
      'The host marker requires an actual layer child: turpinart.com has a "Landscape Paintings" gallery on a bare .landscape container, and a plain class match would have set a painting gallery drifting on scroll. 1 site, 11 sections. 56 frozen transforms + 56 transition:none. z-index (11/10 on the front two layers, constant across all variants) is AUTHORED stacking order and is deliberately NOT stripped — removing it reorders the mountain layers. --offset (0/0.3/0.5/0.8/0.96) is authored config; --scrollPos is a scroll snapshot the M7 driver rewrites with px units.'
  },
  {
    id: 'parallax-translate',
    markers: ['.translate'],
    support: 'runtime',
    strip: {},
    reset: { transform: 'translateY(0px)' },
    evidence:
      'Exactly ONE element in the whole corpus (neudigital why-choose-1882). Reset rather than removed so the existing runtime, which reads its baseline from the inline transform, keeps working unchanged — and so scroll-animations-tests.js needs no edit.'
  },
  {
    id: 'marquee',
    markers: ['.marquee', '#logo-track', '.cs-logo-track', '.logo-scroller .scroller-inner'],
    support: 'runtime',
    strip: { transform: 'translate-only' },
    reset: {},
    stamp: {
      on: '.marquee, #logo-track, .cs-logo-track, .logo-scroller .scroller-inner',
      name: 'data-pb-motion',
      value: 'marquee'
    },
    reverseMarkers: ['.cs-logo-track-reverse'],
    evidence:
      '3 sites. neudigital logo tracks frozen at translate3d(-15px) and translate3d(-2645.69px) — the second is scrolled most of a track-width off, leaving a half-empty logo row. The logo <img> sizing inside the track (height/width/max-width/object-fit) is the only sizing those images have and is NOT in scope: it fails the self-match rule. neudigital ships the tracks as a PAIR — class="cs-logo-track cs-logo-track-reverse" on the second — scrolling opposite directions, hence reverseMarkers (M7 driver). Each track carries its 9 logos tripled (period 9 of 27 children), so the driver can wrap seamlessly at one period width. tcvisualsllc.com (IF-5) adds the client-logo scroller in its rotating-headline section, frozen at translate3d(-50.4px): the same shape — .logo-scroller is overflow:hidden with a mask gradient, .scroller-inner is display:flex/flex-wrap:nowrap/width:max-content/will-change:transform, and its 8 logos are DOUBLED (period 8 of 16), so the M7 driver wraps seamlessly at half the track. The marker is scoped to .logo-scroller because "scroller-inner" is a generic enough class name to appear on something that is not a marquee. This section reached the corpus only in IF-5 — it was one of the id-less sections the parser had been dropping, which is why a two-site pattern was a three-site pattern all along.'
  },
  {
    id: 'swiper',
    markers: [
      '.swiper',
      '.swiper-container',
      '.swiper-wrapper',
      '.swiper-slide',
      '.swiper-slide-shadow-left',
      '.swiper-slide-shadow-right',
      '.swiper-slide-shadow-top',
      '.swiper-slide-shadow-bottom'
    ],
    support: 'static',
    strip: {
      transform: 'any',
      opacity: 'any',
      'transition-duration': 'any',
      'transition-delay': 'any',
      width: 'any',
      height: 'any',
      'z-index': 'any'
    },
    reset: {},
    removeNodes: [
      '.swiper-slide-duplicate',
      '.swiper-slide-shadow-left',
      '.swiper-slide-shadow-right',
      '.swiper-slide-shadow-top',
      '.swiper-slide-shadow-bottom',
      // The collapse is a DOM edit, and a DOM edit does not survive the library
      // that rebuilt the DOM in the first place. ecogardensolution ships
      // swiper-bundle.min.js plus its initializer inside the section markup,
      // still pointing at the source site's CDN; the preview iframe runs with
      // sandbox="allow-scripts" and no script-src CSP, so both load and Swiper
      // re-clones every slide and re-enables the arrows we just took out of the
      // tab order. Collapsing a carousel means removing its runtime too.
      // Scoped to the library and its initializer: the sibling Shopify scripts
      // (product-form, pickup-availability, media-gallery) and the JSON-LD
      // blocks are untouched.
      'script[src*="swiper"]',
      'script[src*="slider"]'
    ],
    neutralize: ['.swiper-button-next', '.swiper-button-prev', '.swiper-pagination'],
    stamp: {
      on: '.swiper, .swiper-container',
      name: 'data-pb-motion-static',
      value: 'swiper'
    },
    evidence:
      '6 sites, 62 sections, 1,211 residue declarations — the largest single source of damage in the corpus. thebeyondclean alone carries 243 inline opacity values, most of them 0, i.e. permanently invisible content, plus 70 duplicate slides Swiper cloned for looping. transform values include rotateX/rotateY/scale, so this entry uses valueKind "any" rather than translate-only.'
  },
  {
    id: 'splide',
    markers: ['.splide', '.splide__list', '.splide__slide'],
    support: 'static',
    strip: { transform: 'any', width: 'any' },
    reset: {},
    removeNodes: ['.splide__slide--clone', 'script[src*="splide"]'],
    neutralize: ['.splide__arrows', '.splide__pagination'],
    stamp: { on: '.splide', name: 'data-pb-motion-static', value: 'splide' },
    evidence:
      '2 sites. youngagainrefinishing carries a track frozen at translateX(-2592px) plus 8 clone slides sized calc(100% + 0rem).'
  },
  {
    id: 'sticky-nav-reveal',
    markers: ['nav.fixed', 'header.fixed'],
    support: 'static',
    strip: { opacity: 'zero-or-one', transform: 'translate-only' },
    reset: {},
    // Excluded from the library-detection census: this is a shared-nav idiom,
    // not a library, and `nav.fixed` matches unrelated authored navs — putting
    // it in the census would drag their declarations into registered scope.
    census: false,
    evidence:
      'yarbug-media.ch ships its shared nav as <nav class="fixed ... transition-all duration-300" style="opacity:0;transform:translateY(-100px)"> — a scroll-triggered slide-down frozen invisible and 100px above the viewport, with no stylesheet opacity rule to fall back on, so the site navigation simply does not render. Surfaced only after the id-less-<header> parser fix brought the site into the corpus. valueKind is deliberately narrow: zero-or-one so an authored opacity:0.9 is untouched, translate-only so a scale or rotate is not.'
  },
  {
    id: 'starwind',
    markers: ['.starwind-accordion-content', '.starwind-select-content'],
    support: 'static',
    strip: { animation: 'any', 'animation-duration': 'any' },
    reset: {},
    evidence:
      '1 site (valc-conseil), Starwind UI accordion/select. JS wrote the reset longhand "animation: auto ease 0s 1 normal none running none", pinning the open/close animation to none. width:var(--starwind-select-trigger-width) is a measured popover width that is left alone — the popover is closed and unsized either way.'
  },
  {
    id: 'progress-bar',
    scope: { site: 'redrockwebdesign.com', sectionId: 'reading-progress' },
    markers: ['div[aria-hidden="true"]'],
    support: 'static',
    strip: { width: 'any' },
    reset: {},
    evidence:
      'The section IS a single <div aria-hidden="true"> with no class, identified only by an Astro scope hash — hence the section-scoped entry. Its own CSS declares width:0 with transition:width .1s, so the frozen inline width:100% paints a permanently full 3px red bar across the top of every page. Stripping restores the stylesheet default, which is the site\'s own resting state.'
  },
  {
    id: 'counter',
    markers: ['[data-count]', '.odometer'],
    support: 'none',
    strip: {},
    reset: {},
    evidence:
      '2 sites, 14 sections. No inline residue — the crawl captured the final counted value as text, which is the correct resting state. Registered so the gate does not report it as an unknown library.'
  }
];

/**
 * Libraries we can recognize but do not handle. Present so a future crawl that
 * introduces one fails the gate loudly instead of shipping frozen animation.
 * None of these currently appear in the corpus.
 */
export const UNSUPPORTED_LIBRARY_MARKERS = [
  { id: 'slick', markers: ['.slick-slider', '.slick-track', '.slick-slide'] },
  { id: 'owl-carousel', markers: ['.owl-carousel', '.owl-stage'] },
  { id: 'wow', markers: ['.wow'] },
  { id: 'sal', markers: ['[data-sal]'] },
  { id: 'animate-css', markers: ['.animate__animated'] },
  { id: 'scrollreveal', markers: ['[data-sr-id]'] },
  { id: 'gsap-scrolltrigger', markers: ['[data-scroll-trigger]', '.gsap-marker-start'] },
  { id: 'lenis', markers: ['.lenis', '[data-lenis-prevent]'] },
  { id: 'locomotive', markers: ['[data-scroll-container]', '[data-scroll-speed]'] }
];

/**
 * Residue that is real but deliberately left in place, each with the reason it
 * cannot be improved. The gate treats these as known, not as violations.
 */
export const KNOWN_GAPS = [
  {
    id: 'language-toggle',
    site: 'oui.digital',
    selector: '.toggle-slider',
    properties: ['transform', 'width'],
    reason:
      'language-toggle indicator pill is sized entirely by JS; its CSS declares position, height and background but no width, so stripping the frozen value leaves it equally invisible. No runtime is planned.'
  },
  {
    id: 'card-tilt',
    site: 'tcvisualsllc.com',
    pattern: 'card-tilt',
    properties: ['--mx', '--my', '--rx', '--ry'],
    reason:
      'package-card pointer tilt is driven by an uncaptured mousemove script. The stylesheet consumes --mx/--my/--rx/--ry, but the rebuilt template ships no writer; the captured neutral values remain as the honest static state and reconstruction is deliberately deferred.'
  },
  {
    id: 'pricing-tabs-indicator',
    site: 'tcvisualsllc.com',
    selector: '.pricing-tabs__indicator',
    properties: ['transform', 'width'],
    reason:
      'the sliding pill behind the active pricing tab is positioned AND sized entirely by an uncaptured script: the stylesheet gives it position/top/bottom/left/background but `width: 0`. Stripping the frozen `width: 216px; transform: translateX(8px)` would not restore a neutral state, it would delete the highlight — and `.pricing-tabs.has-indicator .package-tab.is-active` sets its own background to transparent precisely because the pill draws it, so the active tab would lose its highlight with nothing replacing it. The captured values ARE the resting state for the first tab, which is also the static collapse a template wants. Same shape as oui.digital language-toggle. Tab-switching reconstruction deliberately deferred with the rest of the JS motion work.'
  }
];
